import { createSelector } from '@reduxjs/toolkit';
import { getSelectedFlightData } from './order';
import {
  allFlightsSelector,
  businessFlightsSelector,
} from '../../CheckoutRoutes/PackageCheckout/store/flights/selectors';
import { EXTRAS, EXTRA_SCOPES } from '../constants/extras';

import { extrasByTourIdSelector, mandatoryTOExtrasSelector } from './services';
import { getAltTourById } from '../../CheckoutRoutes/PackageCheckout/store/actualize-alternatives';
import { getMedicalInsurance, getTransfer } from './package';
import type { FlightsData } from '../../CheckoutRoutes/PackageCheckout/types/flights';

const extraSelector = createSelector(
  [
    (state, tourId) => extrasByTourIdSelector(state, tourId),
    (_state, _tourId, name) => name,
  ],
  (extras, name) => extras.find((extra) => extra.code_name === name),
);

export const flightLockSelector = (state, tourId) =>
  extraSelector(state, tourId, EXTRAS.FLIGHT_LOCK);

export const visaRiskSelector = (state, tourId) =>
  extraSelector(state, tourId, EXTRAS.VISA_RISK);

/**
 * Возвращает массив из допуслуг (трансфер и страховка)
 * для альтернативного пакета, только если состав допуслуг лучше, чем
 * в основном пакете
 */
export const servicesToUpgradeSelector = createSelector(
  [
    (state, tourId) => getAltTourById(state, tourId),
    getMedicalInsurance,
    getTransfer,
  ],
  (altTour, origMedInsurance, origTransfer) => {
    const showAltMedInsurance = origMedInsurance
      ? false
      : altTour?.medical_insurance || false;
    const medInsuranceExtraTemplate = {
      name: 'Страховка',
      price: 0,
      scope: EXTRA_SCOPES.TOUR,
    };

    const showAltTransfer = origTransfer ? false : altTour?.transfer || false;
    const transferExtraTemplate = {
      name: 'Трансфер',
      price: 0,
      scope: EXTRA_SCOPES.TOUR,
    };

    const servicesToUpgrade = [
      ...(showAltMedInsurance ? [medInsuranceExtraTemplate] : []),
      ...(showAltTransfer ? [transferExtraTemplate] : []),
    ];
    return servicesToUpgrade;
  },
);

export const flightSummaryItemSelector = createSelector(
  [
    mandatoryTOExtrasSelector,
    flightLockSelector,
    (_state, _tourId, params) => params,
    servicesToUpgradeSelector,
  ],
  (mandatoryExtras, flightLock, params, servicesToUpgrade) => {
    const { hasWorseExtras, couldBeChanged } = params;
    const extrasWithTransferAndInsurance = [
      ...mandatoryExtras,
      ...(!couldBeChanged && flightLock ? [flightLock] : []),
      ...(!hasWorseExtras ? servicesToUpgrade : []),
    ];

    return extrasWithTransferAndInsurance;
  },
);

export const getUpgradedFlight = createSelector(
  [getSelectedFlightData, businessFlightsSelector],
  (selectedFlight, businessFlights): FlightsData | false | object => {
    if (
      !selectedFlight ||
      selectedFlight.to.business ||
      !businessFlights.length
    ) {
      return false;
    }

    return businessFlights.reduce((result, flight) => {
      if (!result && flight.to.flight_id === selectedFlight.to.flight_id) {
        const back = flight.back.find(
          (backFlight) =>
            backFlight.flight_id === selectedFlight.back[0].flight_id,
        );
        if (back) {
          return { to: flight.to, back: [back] };
        }
      }
      return result;
    }, {});
  },
);

export const isChangableFlightSelector = createSelector(
  [
    allFlightsSelector,
    (state, params: { tour_id: string; flight_pair_id: string }) => ({
      hasFlightLock: flightLockSelector(state, params.tour_id),
      tour_id: params.tour_id,
      flight_pair_id: params.flight_pair_id,
    }),
  ],
  (flights, params) => {
    const { tour_id, hasFlightLock, flight_pair_id } = params;

    if (!hasFlightLock) return false;

    const cheapestChangableFlight = flights.find(
      (flight) => flight.back[0].tour_id === tour_id,
    );

    if (!cheapestChangableFlight) return false;

    return cheapestChangableFlight.back[0].flight_pair_id === flight_pair_id;
  },
);
